"use client";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Package, Info, Upload } from "lucide-react";
import axios from "axios";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { getBrands } from "@/state/brand/brand.slice";
import { getCategories } from "@/state/category/category.slice";
import { getUnits } from "@/state/units/unit.slice";
import { getShops } from "@/state/shops/shop.slice";
import { getProduct, updateProduct, reset } from "@/state/products/product.slice";

const API_URL = import.meta.env.VITE_API_URL;

export default function EditProductPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { toast } = useToast();

    const { brands } = useSelector((state) => state.brand);
    const { categories } = useSelector((state) => state.category);
    const { units } = useSelector((state) => state.unit);
    const { shops } = useSelector((state) => state.shop || { shops: [] });
    const { product, isLoading, success, error, message } = useSelector(
        (state) => state.product
    );

    const [formData, setFormData] = useState({
        title: "",
        sku: "",
        barcodeType: "none",
        description: "",
        category_id: "none",
        brand_id: "none",
        unit_id: "none",
        shop_id: "none",
        supplier_id: "none",
        buying_price: 0,
        current_price: 0,
        wholesale_price: 0,
        quantity: 0,
        alertQuantity: 5,
        manageStock: false,
        status: 1,
    });

    const [suppliers, setSuppliers] = useState([]);

    const [isSaving, setIsSaving] = useState(false);
    const [adjustingStock, setAdjustingStock] = useState(false);
    const [stockAdjustment, setStockAdjustment] = useState(0);
    const [productImage, setProductImage] = useState(null);
    const [productBrochure, setProductBrochure] = useState(null);
    const [existingImage, setExistingImage] = useState(null);

    // Fetch product and dependencies
    useEffect(() => {
        dispatch(getProduct(id));
        dispatch(getBrands());
        dispatch(getCategories());
        dispatch(getUnits());
        dispatch(getShops());

        // Fetch suppliers
        const fetchSuppliers = async () => {
            try {
                const res = await fetch(`${API_URL.replace('products', '')}suppliers`, {
                    credentials: 'include'
                });
                const data = await res.json();
                if (data.success) {
                    setSuppliers(data.data);
                }
            } catch (e) {
                console.error("Failed to load suppliers");
            }
        };
        fetchSuppliers();
    }, [dispatch, id]);

    // Populate form when product loads
    useEffect(() => {
        if (product?.data) {
            const p = product.data;
            setFormData({
                title: p.title || "",
                sku: p.sku || "",
                barcodeType: p.barcodeType || "none",
                description: p.description || "",
                category_id: p.category_id?.toString() || "none",
                brand_id: p.brand_id?.toString() || "none",
                unit_id: p.unit_id?.toString() || "none",
                shop_id: p.shop_id?.toString() || "none",
                supplier_id: p.supplier_id?.toString() || "none",
                buying_price: parseFloat(p.buying_price) || 0,
                current_price: parseFloat(p.current_price) || 0,
                wholesale_price: parseFloat(p.wholesale_price) || 0,
                quantity: p.stockQuantity || 0,
                alertQuantity: p.alertQuantity || 5,
                manageStock: p.manageStock || false,
                status: p.status ?? 1,
            });
            setExistingImage(p.feature_image || null);
        }
    }, [product]);

    // Handle success/error
    useEffect(() => {
        if (success && message) {
            toast({
                title: "✓ Success",
                description: message,
                className: "bg-green-50 dark:bg-green-900/30 border-green-200",
            });
            dispatch(reset());
            setIsSaving(false);
        }
        if (error) {
            toast({
                title: "✕ Error",
                description: typeof error === "string" ? error : "An error occurred",
                variant: "destructive",
            });
            dispatch(reset());
            setIsSaving(false);
        }
    }, [success, error, message, toast, dispatch]);

    const handleChange = (field, value) => {
        // Handle special "none" value for optional selects
        const processedValue = value === "none" ? "" : value;
        setFormData((prev) => ({ ...prev, [field]: processedValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            toast({
                title: "Validation Error",
                description: "Product title is required",
                variant: "destructive",
            });
            return;
        }

        setIsSaving(true);
        dispatch(
            updateProduct({
                id,
                ...formData,
                barcodeType: formData.barcodeType === "none" ? null : formData.barcodeType,
                category_id: formData.category_id === "none" ? null : formData.category_id,
                brand_id: formData.brand_id === "none" ? null : formData.brand_id,
                unit_id: formData.unit_id === "none" ? null : formData.unit_id,
                shop_id: formData.shop_id === "none" ? null : formData.shop_id,
                supplier_id: formData.supplier_id === "none" ? null : formData.supplier_id,
            })
        );
    };

    const handleStockAdjust = async () => {
        if (stockAdjustment === 0) return;

        setAdjustingStock(true);
        try {
            await axios.patch(
                `${API_URL}products/${id}/stock`,
                { adjustment: stockAdjustment, reason: "Manual adjustment from edit page" },
                { withCredentials: true }
            );
            toast({
                title: "✓ Success",
                description: `Stock ${stockAdjustment > 0 ? "added" : "removed"} successfully`,
                className: "bg-green-50 dark:bg-green-900/30 border-green-200",
            });
            setStockAdjustment(0);
            dispatch(getProduct(id));
        } catch (err) {
            toast({
                title: "✕ Error",
                description: err.response?.data?.message || "Failed to adjust stock",
                variant: "destructive",
            });
        } finally {
            setAdjustingStock(false);
        }
    };

    const formatCurrency = (amount) => `GHS ${parseFloat(amount || 0).toFixed(2)}`;

    if (isLoading && !product) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-3xl font-bold">Edit Product</h1>
                    <p className="text-muted-foreground">{formData.title || "Loading..."}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Details */}
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Product Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title">Product Name *</Label>
                                    <Input
                                        id="title"
                                        value={formData.title}
                                        onChange={(e) => handleChange("title", e.target.value)}
                                        placeholder="Enter product name"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="sku" className="flex items-center">
                                        SKU
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p className="text-sm">
                                                    Stock Keeping Unit - unique identifier for product tracking
                                                </p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </Label>
                                    <Input
                                        id="sku"
                                        value={formData.sku}
                                        onChange={(e) => handleChange("sku", e.target.value)}
                                        placeholder="Enter SKU"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    placeholder="Product description"
                                    rows={3}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Barcode Type</Label>
                                    <Select
                                        value={formData.barcodeType || "none"}
                                        onValueChange={(val) => handleChange("barcodeType", val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select barcode type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Barcode</SelectItem>
                                            <SelectItem value="code128">Code 128 (C128)</SelectItem>
                                            <SelectItem value="ean13">EAN-13</SelectItem>
                                            <SelectItem value="upc">UPC</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Unit</Label>
                                    <Select
                                        value={formData.unit_id || "none"}
                                        onValueChange={(val) => handleChange("unit_id", val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select unit" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Unit</SelectItem>
                                            {units?.map((unit) => (
                                                <SelectItem key={unit.id} value={unit.id.toString()}>
                                                    {unit.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Category</Label>
                                    <Select
                                        value={formData.category_id || "none"}
                                        onValueChange={(val) => handleChange("category_id", val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Category</SelectItem>
                                            {categories?.map((cat) => (
                                                <SelectItem key={cat.id} value={cat.id.toString()}>
                                                    {cat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Brand</Label>
                                    <Select
                                        value={formData.brand_id || "none"}
                                        onValueChange={(val) => handleChange("brand_id", val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Brand</SelectItem>
                                            {brands?.map((brand) => (
                                                <SelectItem key={brand.id} value={brand.id.toString()}>
                                                    {brand.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Shop</Label>
                                    <Select
                                        value={formData.shop_id || "none"}
                                        onValueChange={(val) => handleChange("shop_id", val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select shop" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">All Shops</SelectItem>
                                            {shops?.map((shop) => (
                                                <SelectItem key={shop.id} value={shop.id.toString()}>
                                                    {shop.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Supplier (Optional)</Label>
                                    <Select
                                        value={formData.supplier_id || "none"}
                                        onValueChange={(val) => handleChange("supplier_id", val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select supplier" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Supplier</SelectItem>
                                            {suppliers?.map((supplier) => (
                                                <SelectItem key={supplier.id} value={supplier.id.toString()}>
                                                    {supplier.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Pricing */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Pricing</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="buying_price">Buying Price (GHS)</Label>
                                <Input
                                    id="buying_price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.buying_price}
                                    onChange={(e) =>
                                        handleChange("buying_price", parseFloat(e.target.value) || 0)
                                    }
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="current_price">Retail Price (GHS)</Label>
                                <Input
                                    id="current_price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.current_price}
                                    onChange={(e) =>
                                        handleChange("current_price", parseFloat(e.target.value) || 0)
                                    }
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="wholesale_price">Wholesale Price (GHS)</Label>
                                <Input
                                    id="wholesale_price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.wholesale_price}
                                    onChange={(e) =>
                                        handleChange("wholesale_price", parseFloat(e.target.value) || 0)
                                    }
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Product Image */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Product Image</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4">
                                {existingImage && (
                                    <div className="relative">
                                        <img
                                            src={existingImage}
                                            alt="Current product image"
                                            className="w-32 h-32 object-cover rounded border"
                                        />
                                        <span className="text-xs text-muted-foreground mt-1 block">Current image</span>
                                    </div>
                                )}
                                <div className="flex-1">
                                    <label
                                        htmlFor="product-image"
                                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-slate-600 border-dashed cursor-pointer bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                                    >
                                        <div className="flex flex-col items-center justify-center py-4">
                                            <Upload className="w-8 h-8 mb-2 text-gray-400 dark:text-gray-500" />
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                <span className="font-semibold">Click to upload</span> new image
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                PNG, JPG or GIF (MAX. 800x400px)
                                            </p>
                                        </div>
                                        <input
                                            id="product-image"
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => setProductImage(e.target.files[0])}
                                        />
                                    </label>
                                    {productImage && (
                                        <p className="text-sm text-green-600 mt-2">
                                            New image selected: {productImage.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Product Brochure */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Product Brochure</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <label
                                htmlFor="product-brochure"
                                className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-slate-600 border-dashed cursor-pointer bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                            >
                                <div className="flex flex-col items-center justify-center py-4">
                                    <Upload className="w-8 h-8 mb-2 text-gray-400 dark:text-gray-500" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Click to upload</span> brochure
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        PDF, CSV, ZIP, DOC, DOCX (MAX. 5MB)
                                    </p>
                                </div>
                                <input
                                    id="product-brochure"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.csv,.zip,.doc,.docx"
                                    onChange={(e) => setProductBrochure(e.target.files[0])}
                                />
                            </label>
                            {productBrochure && (
                                <p className="text-sm text-green-600 mt-2">
                                    Brochure selected: {productBrochure.name}
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Status */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Select
                                value={formData.status.toString()}
                                onValueChange={(val) => handleChange("status", parseInt(val))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">Active</SelectItem>
                                    <SelectItem value="0">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>

                    {/* Stock Management */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Package className="h-5 w-5" />
                                Stock Management
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Manage Stock</span>
                                <Switch
                                    id="manage-stock"
                                    checked={formData.manageStock}
                                    onCheckedChange={(checked) => handleChange("manageStock", checked)}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Current Stock</span>
                                <Badge
                                    variant={
                                        formData.quantity === 0
                                            ? "destructive"
                                            : formData.quantity <= formData.alertQuantity
                                                ? "warning"
                                                : "success"
                                    }
                                    className={
                                        formData.quantity <= formData.alertQuantity && formData.quantity > 0
                                            ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                                            : formData.quantity > formData.alertQuantity
                                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                                : ""
                                    }
                                >
                                    {formData.quantity} units
                                </Badge>
                            </div>

                            <div className="space-y-2">
                                <Label>Quick Adjust</Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        value={stockAdjustment}
                                        onChange={(e) => setStockAdjustment(parseInt(e.target.value) || 0)}
                                        placeholder="+/- quantity"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleStockAdjust}
                                        disabled={adjustingStock || stockAdjustment === 0}
                                    >
                                        {adjustingStock ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            "Adjust"
                                        )}
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Use positive to add, negative to remove
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="alertQuantity" className="flex items-center">
                                    Alert Quantity
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p className="text-sm">
                                                Get notified when stock falls below this level
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </Label>
                                <Input
                                    id="alertQuantity"
                                    type="number"
                                    min="0"
                                    value={formData.alertQuantity}
                                    onChange={(e) =>
                                        handleChange("alertQuantity", parseInt(e.target.value) || 0)
                                    }
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                        <Button type="submit" disabled={isSaving} className="w-full">
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="w-full"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}
